import React from 'react';
import Link from '../Link';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Badge, IconButton } from '@mui/material';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import CelebrationOutlinedIcon from '@mui/icons-material/CelebrationOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import RedeemOutlinedIcon from '@mui/icons-material/RedeemOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import InstagramIcon from '@mui/icons-material/Instagram';
import PinterestIcon from '@mui/icons-material/Pinterest';
import TiktokIcon from '../TiktokIcon';
import { getWhatsappLink } from '../../utils/utils';
import useHook from './useHook';
import styles from './styles.module.scss';

type MenuFlotanteProps = {
    setMenuIsOpen: (open: boolean) => void
    itemsInW: number
    // Raw Strapi tematica entities (not modeled yet), same shape the desktop Festividades dropdown consumes.
    festividades: any[]
}

type MenuItemData = {
    label: string
    href: string
    icon: React.ReactNode
    external?: boolean
}

const comprarAntesDeFestividades: MenuItemData[] = [
  { label: 'Novedades', href: '/productos/novedades', icon: <NewReleasesOutlinedIcon /> },
  { label: 'Temáticas', href: '/tematicas', icon: <CelebrationOutlinedIcon /> },
];

const comprarDespuesDeFestividades: MenuItemData[] = [
  { label: 'Ver todo', href: '/tienda', icon: <StorefrontOutlinedIcon /> },
  { label: 'Bonetes', href: '/productos/tipo/bonete', icon: <RedeemOutlinedIcon /> },
  { label: 'Inspo', href: '/inspo', icon: <AutoAwesomeOutlinedIcon /> },
  { label: 'Mayoristas', href: '/catalogo-mayorista', icon: <BusinessCenterOutlinedIcon /> },
];

const cuenta: MenuItemData[] = [
  { label: 'Mi cuenta', href: '/mi-cuenta', icon: <AccountCircleOutlinedIcon /> },
  { label: 'Mi wishlist', href: '/wishlist', icon: <FavoriteBorderIcon /> },
];

const ayuda: MenuItemData[] = [
  { label: 'Contacto por WhatsApp', href: getWhatsappLink(), icon: <ChatBubbleOutlineOutlinedIcon />, external: true },
  { label: 'Contacto por email', href: 'mailto:info@we-celebrate.com.ar', icon: <MailOutlineOutlinedIcon />, external: true },
];

const redes = [
  { label: 'Instagram', href: 'https://www.instagram.com/wecelebrateok/?hl=es', icon: <InstagramIcon /> },
  { label: 'Pinterest', href: 'https://ar.pinterest.com/wecelebratear/', icon: <PinterestIcon /> },
  { label: 'Tik Tok', href: 'https://www.tiktok.com/@wecelebrateok?_r=1&_t=ZM-91g2dkY3nxY', icon: <TiktokIcon /> },
];

const MenuFlotante = ({ setMenuIsOpen, itemsInW, festividades }: MenuFlotanteProps) => {

  const {
    open,
    festividadesOpen,
    setFestividadesOpen,
    menuRef,
    closeButtonRef,
    handleClose,
  } = useHook({ setMenuIsOpen });

  return (
    <>
      <div className={`${styles.overlay} ${open ? styles.overlayOpen : ''}`} onClick={handleClose} />
      <div className={`${styles.menu} ${open ? styles.menuOpen : ''}`} ref={menuRef} role="dialog" aria-modal="true" aria-label="Menú">
        <div className={styles.firstRow}>
          <span className={styles.tituloMenu}>Menú 🎉</span>
          <IconButton ref={closeButtonRef} className={styles.closeButton} color="black" style={{ padding: '12px', opacity: 0.7 }} onClick={handleClose} aria-label="Cerrar menú">
            <CloseIcon />
          </IconButton>
        </div>
        <div className={styles.itemsContainer}>
          <span className={styles.seccionTitulo}>Comprar</span>
          {comprarAntesDeFestividades.map((item) => (
            <Link key={item.href} href={item.href}>
              <div className={styles.item}>{item.icon}{item.label}</div>
            </Link>
          ))}

          <button
            className={styles.expandableItem}
            onClick={() => setFestividadesOpen((prev) => !prev)}
            aria-expanded={festividadesOpen}
            aria-label="Festividades"
          >
            <EventOutlinedIcon />
                        Festividades
            <span className={`${styles.chevron} ${festividadesOpen ? styles.chevronOpen : ''}`}>
              <ExpandMoreIcon />
            </span>
          </button>
          <div className={`${styles.subItemsWrapper} ${festividadesOpen ? styles.subItemsWrapperOpen : ''}`} aria-hidden={!festividadesOpen}>
            <div className={styles.subItemsList}>
              {festividades?.map((item: any) => (
                <Link key={item.id} href={`/productos/tematica/${item.attributes.slug}`}>
                  <div className={styles.subItem}>{item.attributes.nombre}</div>
                </Link>
              ))}
            </div>
          </div>

          {comprarDespuesDeFestividades.map((item) => (
            <Link key={item.href} href={item.href}>
              <div className={styles.item}>{item.icon}{item.label}</div>
            </Link>
          ))}

          <span className={styles.seccionTitulo}>Mi cuenta</span>
          {cuenta.map((item) => (
            <Link key={item.href} href={item.href}>
              <div className={styles.item}>
                {item.label === 'Mi wishlist' ? (
                  <Badge badgeContent={itemsInW} color="black">
                    {item.icon}
                  </Badge>
                ) : item.icon}
                {item.label}
              </div>
            </Link>
          ))}

          <span className={styles.seccionTitulo}>Ayuda</span>
          {ayuda.map((item) => (
            <Link key={item.href} href={item.href} external={item.external}>
              <div className={styles.item}>{item.icon}{item.label}</div>
            </Link>
          ))}

          <span className={styles.seccionTitulo}>Seguinos</span>
          <div className={styles.redesRow}>
            {redes.map((red) => (
              <a key={red.href} className={styles.redSocial} href={red.href} target="_blank" rel="noopener noreferrer" aria-label={red.label}>
                {red.icon}
              </a>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default MenuFlotante;
